import React, { memo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import DoNotDisturbAltRoundedIcon from '@mui/icons-material/DoNotDisturbAltRounded';
import CommonTypography from '@src/new-components/Typography';
import { resolveFaceUrl, loadCaptionFont } from '@src/services/caption';
import useFontCatalog from '@src/hooks/useFontCatalog';
import actions from '@src/redux/actions';
import { CAPTION_STYLE_NONE, DUBBING_PANEL_VIEW } from '@src/constants/dubbing';
import { HISTORY_LABEL, HISTORY_SCOPE } from '@src/constants/history';
import useCaptionTemplates from '../../useCaptionTemplates';
import { StyledStyleList, StyledNoCaptionOption } from './index.style';
import StyleCard from './StyleCard';

const StyleList = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const fonts = useFontCatalog();
  const templates = useCaptionTemplates();
  const selectedId = useSelector(
    (state) => state.project.videoSettings?.captionStyle?.styleId,
  );

  const customStyle = useSelector(
    (state) => state.project.videoSettings?.customCaptionStyle,
  );
  // Register the style fonts or every card falls back to the same system one.
  // Registering repaints the text on its own, so no setState here.
  useEffect(() => {
    [...templates, customStyle].forEach((style) => {
      const text = style?.config?.text;
      loadCaptionFont(text, resolveFaceUrl(fonts, text));
    });
  }, [fonts, templates, customStyle]);

  // Ổn định qua các lần render để memo của StyleCard có tác dụng.
  const handleSelect = useCallback(
    (styleId, config) =>
      dispatch(
        actions.project.applyCaptionStyle(styleId, config, {
          history: {
            commit: true,
            scope: HISTORY_SCOPE.DUBBING,
            label: HISTORY_LABEL.APPLY_CAPTION_STYLE,
          },
        }),
      ),
    [dispatch],
  );

  const handleConfigure = useCallback(
    (styleId, config) => {
      handleSelect(styleId, config);
      dispatch(
        actions.layout.setDubbingPanelView(DUBBING_PANEL_VIEW.CAPTION_CONFIG),
      );
    },
    [dispatch, handleSelect],
  );

  const configureLabel = t('captionConfigure');

  return (
    <StyledStyleList>
      <StyledNoCaptionOption
        type="button"
        isSelected={selectedId === CAPTION_STYLE_NONE}
        onClick={() => handleSelect(CAPTION_STYLE_NONE)}
      >
        <DoNotDisturbAltRoundedIcon />
        <CommonTypography
          className="no-caption-label"
          variant="body-16-semibold"
        >
          {t('captionStyleNone')}
        </CommonTypography>
      </StyledNoCaptionOption>

      {customStyle && (
        <StyleCard
          styleId={customStyle.styleId}
          label={t('captionStyleCustom')}
          config={customStyle.config}
          isSelected={selectedId === customStyle.styleId}
          configureLabel={configureLabel}
          onSelect={handleSelect}
          onConfigure={handleConfigure}
        />
      )}

      {templates.map((template) => (
        <StyleCard
          key={template.code}
          styleId={template.code}
          label={template.name}
          config={template.config}
          isSelected={selectedId === template.code}
          configureLabel={configureLabel}
          onSelect={handleSelect}
          onConfigure={handleConfigure}
        />
      ))}
    </StyledStyleList>
  );
};

export default memo(StyleList);
